import { useCallback, useMemo, useState } from "react";
import type { ComponentProps } from "react";

import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components/foundations";

import categoryService from "app/common/services/categoryService";
import type { ICategory, IFlattenedCategory } from "app/common/services/categoryService.types";
import { translate } from "app/filters/i18nFilter";
import { useDebouncedSetSearchTerm } from "app/pages/digital-marketing/ad-intelligence/common/ads/hooks/useDebouncedSetSearchTerm";
import { IndustryDataAvailabilityPageBanner } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { IIndustryPublisherRow } from "app/pages/digital-marketing/ad-intelligence/common/overview/industry/top-publishers/useIndustryTopPublishersTable";
import { useIndustryTopPublishersTable } from "app/pages/digital-marketing/ad-intelligence/common/overview/industry/top-publishers/useIndustryTopPublishersTable";
import {
    buildSearchFilter,
    FIRST_PAGE,
    getNoDataObject,
    PAGE_SIZE_LARGE,
} from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import DurationService, { BasicDurations } from "app/services/DurationService";
import type { dateForTooltip } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getColumns } from "./IndustryPublishersColumns";
import { IndustryPublishersFilters } from "./IndustryPublishersFilters";
import type { IIndustryPublishersFilterState } from "./IndustryPublishersFilters";

// Rows are spend-ranked by default, so the table opens sorted by spend share, descending.
const initialTableSort: ISortProps = { asc: false, sort: "spendShare" };

const SEARCH_DEBOUNCE_DELAY = 600;

// Maps camelCase share-column keys to the endpoint's snake_case sort keys.
const SERVER_SORT_KEY_BY_COLUMN: Record<string, string> = {
    spendShare: "spend",
    impressionsShare: "impressions",
    visitsShare: "visits",
};

interface IIndustryPublishersProps {
    category: IFlattenedCategory;
}

export const IndustryPublishers = ({ category }: IIndustryPublishersProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    // `includeTerms` is an array route param (string[]); take the first term.
    const [search, setSearch] = useState<string>(params.includeTerms?.[0] ?? "");
    const [sort, setSort] = useState<ISortProps>({
        sort: params.sort ?? initialTableSort.sort,
        asc: params.asc ? params.asc === "true" : initialTableSort.asc,
    });
    const debouncedSetSearchTerm = useDebouncedSetSearchTerm(SEARCH_DEBOUNCE_DELAY);
    // Selected industry is a category `forApi` (`$`-prefixed); the endpoint's
    // `publisherIndustries` wants the "Category/SubCategory" form without the `$`.
    const [selectedIndustry, setSelectedIndustry] = useState<string | undefined>(undefined);

    const serverSortKey = SERVER_SORT_KEY_BY_COLUMN[sort.sort] ?? SERVER_SORT_KEY_BY_COLUMN[initialTableSort.sort];
    // The endpoint exact-matches "Category/SubCategory" and does NOT cascade a
    // parent to its children. A sub-category value maps 1:1; a parent value is
    // expanded into itself (empty-subcategory rows) plus every child so selecting
    // a top-level industry returns all of its publishers.
    const publisherIndustries = useMemo<string[] | undefined>(() => {
        if (!selectedIndustry) {
            return undefined;
        }
        const value = selectedIndustry.slice(1); // strip the "$" prefix
        if (value.includes("/")) {
            return [value];
        }
        const parent = categoryService.getCategories(false).find((item) => item.forApi === selectedIndustry);
        const childValues = Array.isArray(parent?.children)
            ? parent.children.map((child: ICategory) => child.forApi.slice(1))
            : [];
        return [value, ...childValues];
    }, [selectedIndustry]);
    const { rows, totalCount, isLoading, isError, availability } = useIndustryTopPublishersTable(
        category,
        currentPage,
        PAGE_SIZE_LARGE,
        search,
        serverSortKey,
        sort.asc,
        publisherIndustries,
    );

    const isWindow = Boolean(
        DurationService.getDurationData(params.duration ?? BasicDurations.LAST_TWENTY_EIGHT_DAYS).raw.isWindow,
    );
    const durationTooltipParams = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(
                    DurationService.getDurationData(params.duration ?? BasicDurations.LAST_TWENTY_EIGHT_DAYS)
                        .forTooltip as dateForTooltip,
                ).map(([key, item]) => [key, decodeURIComponent(item)]),
            ) as dateForTooltip,
        [params.duration],
    );

    const handlePublisherClick = useCallback(
        (publisher: string) => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", `Industry_publishers/Publisher/${publisher}`);
            void publisherAnalysisNavigation.overview.go({ ...params, key: publisher });
        },
        [params],
    );

    const columns = useMemo(
        () =>
            getColumns(
                totalCount,
                currentPage,
                PAGE_SIZE_LARGE,
                durationTooltipParams,
                isWindow,
                sort,
                handlePublisherClick,
            ),
        [totalCount, currentPage, durationTooltipParams, isWindow, sort, handlePublisherClick],
    );

    const handleSearchInputChange = (value: string) => {
        void proNavigator.updateQueryParams({ ...params, includeTerms: value }, { reload: false });
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value}`);
        setCurrentPage(FIRST_PAGE);
        // Debounce the query-driving state so we don't refetch on every keystroke.
        debouncedSetSearchTerm(value, setSearch);
    };

    const handleIndustryChange = (value: IIndustryPublishersFilterState) => {
        setSelectedIndustry(value.publisherIndustry);
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Filter Publisher Industry/${value.publisherIndustry ?? "cleared"}`,
        );
        setCurrentPage(FIRST_PAGE);
    };

    const handleSort: ComponentProps<typeof Table<IIndustryPublisherRow>>["onSort"] = (sorter) => {
        void proNavigator.updateQueryParams(
            { ...params, sort: sorter.columnKey, asc: sorter.order === "ascend" },
            { reload: false },
        );
        setSort({ sort: sorter.columnKey as string, asc: sorter.order === "ascend" });
        setCurrentPage(FIRST_PAGE);
    };

    const noDataObject = getNoDataObject(isError);

    return (
        <>
            <IndustryDataAvailabilityPageBanner availability={availability} />
            <Box
                width="100%"
                height="initial"
                background={colorsPalettes.carbon[0]}
                borderRadius="6px"
                data-automation="industry-publishers-table"
            >
                <Table
                    columnConfig={columns}
                    data={rows}
                    isLoading={isLoading}
                    loaderRowCount={13}
                    onSort={handleSort}
                    localStorageKey="AdIntelligenceIndustryPublishers"
                    pagination={{
                        pageSize: PAGE_SIZE_LARGE,
                        total: totalCount,
                        current: currentPage,
                        onChange: (_page, _pageSize, nextPage) => setCurrentPage(nextPage),
                    }}
                    noData={noDataObject}
                >
                    <Table.TableTop borderBottom>
                        <Table.TableTop.Filters
                            filters={buildSearchFilter({
                                initialValue: search,
                                placeholder: translate(
                                    "ad.intelligence.advertiser.publishers.table.search.placeholder",
                                ),
                                onSearchChange: handleSearchInputChange,
                            })}
                            factoryFilters={
                                <IndustryPublishersFilters
                                    selectedIndustry={selectedIndustry}
                                    onFilterChange={handleIndustryChange}
                                />
                            }
                        />
                    </Table.TableTop>
                </Table>
            </Box>
        </>
    );
};
